import Controller, { inject as controller } from '@ember/controller';
import { action, setProperties } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import type HotelRevenueInsightController from 'lumen/controllers/hotel/revenue-insight';
import { KPI_CATEGORIES } from 'lumen/utils/constants/ri-constant-values';
import dateRangeDisplay from 'lumen/utils/dates/date-range-display';
import {
  segmentationIdFormatter,
  segmentationValueFormatter,
} from 'lumen/utils/revenue-insight/segmentation-value-formatter';

export default class HotelRevenueInsightBreakdownDetailController extends Controller {
  // @ts-expect-error: Incorrect type
  queryParams = [
    {
      segmentationId: { scope: 'controller' },
    },
  ];

  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @controller('hotel.revenue-insight')
  declare parentController: HotelRevenueInsightController;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get detailTabParams() {
    const segmentations = {
      ...this.parentController.breakdowns,
      pickup: this.parentController.pickup,
    };

    const params = {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      allModifiedSegmentationObjects: segmentations[this.segmentationId],
      blockDisplayName: this.segmentationPrettyName,
      isLoading: this.parentController.isModelLoading,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      visibleKpis:
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.parentController.visibleKpisPerBreakdown[this.segmentationId],
    };

    setProperties(params, this.filterParams);

    return params;
  }

  /**
   * Used to display the filters in the header
   * @return { array }   : object containing all info needed for the filters
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterParams() {
    return {
      parsedSegmentationFilters:
        this.parentController.parsedSegmentationFilters,
      segmentationDisplayNameOverride: this.segmentationDisplayNameOverride,
      parsedStayDateFilter: this.parentController.parsedStayDateFilter,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      kpi: this.kpi,
      kpiCategory: this.parentController.kpiCategory,
      reference: this.parentController.reference,
      range: this.parentController.range,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      blockName: this.segmentationId,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterText() {
    // @ts-expect-error: Incorrect type
    const ignoreNetComparison = this.segmentationId === RI_BREAKDOWNS.PICKUP;

    const MAX_NB_OF_FILTERS_IN_HEADERTEXT = 4;

    const headerSegments = [];

    const {
      range,
      reference,
      kpiCategory,
      parsedSegmentationFilters,
      parsedStayDateFilter,
    } = this.parentController;

    const vsTextOption =
      this.riSelections.isNetComparison && !ignoreNetComparison
        ? `${this.intl.t('revenue_insight.net')} ${reference}`
        : reference;

    headerSegments.push(
      `${dateRangeDisplay(range?.start, range?.end, this.intl)} ${this.intl.t(
        'revenue_insight.vs',
      )} ${vsTextOption}`,
    );

    if (this.riSelections.isSingleProperty) {
      headerSegments.push(
        this.riSelections.isCancellationsOnlyView
          ? this.intl.t('revenue_insight.view_selection.cancellations_only')
          : this.intl.t('revenue_insight.view_selection.net_bookings'),
      );
    }

    headerSegments.push(
      this.riSelections.isCancellationsOnlyView &&
        kpiCategory === KPI_CATEGORIES.TOTAL
        ? this.intl.t(`revenue_insight.${kpiCategory}_cancelled`)
        : this.intl.t(`revenue_insight.${kpiCategory}`),
    );

    const activeFilterKeys = Object.keys(parsedSegmentationFilters);
    let strActiveFilters = '';

    let nbOfActiveFilters = 0;
    activeFilterKeys.forEach((key) => {
      if (parsedSegmentationFilters[key]?.length) {
        nbOfActiveFilters += parsedSegmentationFilters[key].length;
      }
    });

    if (nbOfActiveFilters) {
      const filtersToShow = activeFilterKeys.slice(
        0,
        MAX_NB_OF_FILTERS_IN_HEADERTEXT,
      );

      filtersToShow.forEach((key, index, array) => {
        const isLastFilterKey = index === array.length - 1;

        parsedSegmentationFilters[key]?.forEach(
          (filter, indexFilterArr, filterArray) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const segmentationValueKey =
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              this.segmentationDisplayNameOverride?.[key]?.[filter] ?? filter;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const filterValue = segmentationValueFormatter(
              key,
              segmentationValueKey,
              {
                intl: this.intl,
                isFuture: this.riSelections.isToDateInFuture,
              },
            );

            const isLastFilterValue = indexFilterArr === filterArray.length - 1;

            if (isLastFilterKey && isLastFilterValue) {
              strActiveFilters += `${this.intl.t(`revenue_insight.${key}`)}: ${filterValue}`;
            } else {
              // \u00A0 is unicode alternative for non-breaking space. Allows us to have more than once space before and after the separator.
              strActiveFilters += `${this.intl.t(
                `revenue_insight.${key}`,
              )}: ${filterValue}\u00A0\u00A0-\u00A0\u00A0`;
            }
          },
        );
      });

      if (nbOfActiveFilters > MAX_NB_OF_FILTERS_IN_HEADERTEXT) {
        strActiveFilters += `\u00A0\u00A0-\u00A0\u00A0${this.intl.t(
          'revenue_insight.expand_view.filter_limit_reached',
          { count: nbOfActiveFilters - MAX_NB_OF_FILTERS_IN_HEADERTEXT },
        )}`;
      }
    } else {
      strActiveFilters = this.intl.t('revenue_insight.expand_view.no_filters');
    }

    // Are staydate filters active?
    if (parsedStayDateFilter) {
      strActiveFilters += `\u00A0\u00A0-\u00A0\u00A0${this.intl.t(
        'revenue_insight.impact_date',
      )}: ${dayjs
        .utc(parsedStayDateFilter?.startDate)
        .format('ll')}\u00A0\u00A0-\u00A0\u00A0${dayjs
        .utc(parsedStayDateFilter?.endDate)
        .format('ll')}`;
    }

    headerSegments.push(strActiveFilters);

    // \u00A0 is unicode alternative for non-breaking space. Allows us to have more than once space before and after the separator.
    return headerSegments.join('\u00A0\u00A0-\u00A0\u00A0');
  }

  // The KPI we use in the breakdown detail is the same as the KPI we show data for in the breakdown.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpi() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.parentController.kpiPerBreakdown[this.segmentationId];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get modalTitle() {
    return `${this.segmentationPrettyName} ${this.intl.t(
      'revenue_insight.expand_view.modal_title',
    )}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationDisplayNameOverride() {
    const segmentationDisplayNameOverride =
      this.parentController.filterDisplayNameOverride;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    segmentationDisplayNameOverride[this.segmentationId] = {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      ...(segmentationDisplayNameOverride[this.segmentationId] ?? {}),
    };

    return segmentationDisplayNameOverride;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationPrettyName() {
    return this.intl.t(
      // @ts-expect-error: Incorrect type
      segmentationIdFormatter(this.segmentationId, this.riSelections),
    );
  }

  @action redirectOnClose(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.revenue-insight');
  }

  @action triggerExcelExportFromModal(): void {
    const days = dayjs(this.parentController.toDateMoment).diff(
      this.parentController.fromDateMoment,
      'day',
    );

    this.usageAnalytics.track('Data Exported', {
      eventTrigger: 'breakdown-detail-modal',
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      exportType: this.segmentationId,
      days,
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.parentController.exportSegmentData(this.segmentationId);
  }
}
